import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { CopyId, ErrorState } from '@bb/ui';
import { ApiError } from '../../api/client.ts';
import { Can } from '../access/Can.tsx';
import { AlertRulesPanel } from '../alerts/AlertRulesPanel.tsx';
import { formatCount } from '../audience/ChannelReachBreakdown.tsx';
import { CommentsSidebar, useStepCommentCounts } from '../comments/CommentsSidebar.tsx';
import { useRecordView } from '../home/useRecordView.ts';
import { WhereUsedPanel } from '../operations/WhereUsedPanel.tsx';
import { formatWhen } from '../segments/SegmentsListPage.tsx';
import { CanvasActions } from './CanvasActions.tsx';
import { CanvasLifecycleActions } from './CanvasLifecycleActions.tsx';
import { ENTRY_LABELS } from './CanvasListPage.tsx';
import { CanvasStatusBadge } from './CanvasStatusBadge.tsx';
import { CanvasWizard } from './wizard/CanvasWizard.tsx';

/**
 * A Canvas's status, run controls (pause, resume, stop), versions, entries and where it is used (BB-109, BB-110), with
 * the wizard that edits and launches it (BB-110).
 */
export function CanvasPage() {
  const { environmentId, canvasId } = useParams({ from: '/env/$environmentId/canvas/$canvasId' });
  const { api } = useRouteContext({ from: '/env/$environmentId/canvas/$canvasId' });
  const view = useQuery({ queryKey: ['canvases', environmentId, 'one', canvasId], queryFn: () => api.getCanvas(environmentId, canvasId) });
  const stepComments = useStepCommentCounts(environmentId, canvasId);
  useRecordView(environmentId, 'canvas', canvasId);
  if (view.isPending) return <p aria-busy="true">Loading Canvas…</p>;
  if (view.isError) return <ErrorState title="Canvas could not be loaded" message={view.error instanceof ApiError ? view.error.message : 'Something went wrong.'} onRetry={() => void view.refetch()} />;
  const c = view.data;
  return (
    <section>
      <p>
        <Link to="/env/$environmentId/canvas" params={{ environmentId }}>
          ← Canvas
        </Link>
      </p>
      <h1>
        {c.name} <CanvasStatusBadge canvas={c} />
      </h1>
      <CopyId label="API id" value={c.apiId} />
      <Can permission="reports.view">
        <p>
          <Link to="/env/$environmentId/canvas/$canvasId/analytics" params={{ environmentId, canvasId }} search={{}}>
            Analytics
          </Link>{' '}
          <span className="hint">— numbers by step, stuck entries and push performance</span>
        </p>
      </Can>
      <CanvasLifecycleActions environmentId={environmentId} canvas={c} />
      <CanvasActions environmentId={environmentId} canvas={c} layout="toolbar" />
      <dl className="facts">
        <dt>Entry type</dt>
        <dd>{c.entryType ? ENTRY_LABELS[c.entryType] : 'Set on publish'}</dd>
        <dt>Steps in the draft</dt>
        <dd>{c.stepCount}</dd>
        <dt>Running version</dt>
        <dd>{c.publishedVersion ? `Version ${c.publishedVersion}` : 'Not launched yet'}</dd>
        <dt>Entries in progress</dt>
        <dd>{formatCount(c.activeEntries)}</dd>
        <dt>Failed entries</dt>
        <dd>{formatCount(c.failedEntries)}</dd>
        <dt>Sent</dt>
        <dd>{formatCount(c.sent)}</dd>
        {c.stoppedAt && (
          <>
            <dt>Stopped</dt>
            <dd>
              {formatWhen(c.stoppedAt)}
              {c.stopReason ? ` — ${c.stopReason}` : ''}
            </dd>
          </>
        )}
      </dl>
      <CanvasWizard environmentId={environmentId} canvas={c} />
      <h2>Steps</h2>
      {c.steps.length === 0 ? (
        <p className="hint">The draft has no steps yet.</p>
      ) : (
        <ul aria-label="Steps">
          {c.steps.map((step) => (
            <li key={step.id}>
              {step.name} <span className="hint">({step.type})</span>
              {stepComments[step.id] ? (
                <span className="tag" aria-label={`${stepComments[step.id]} open comments`}>
                  💬 {stepComments[step.id]}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <h2>Versions</h2>
      {c.versions.length === 0 ? (
        <p className="hint">No published versions.</p>
      ) : (
        <ol aria-label="Versions" reversed>
          {c.versions.map((v) => (
            <li key={v.version}>
              Version {v.version}: published {formatWhen(v.publishedAt)}
              {v.publishedBy?.email ? ` by ${v.publishedBy.email}` : ''}
            </li>
          ))}
        </ol>
      )}
      <AlertRulesPanel environmentId={environmentId} objectType="canvas" objectId={canvasId} />
      <WhereUsedPanel environmentId={environmentId} objectType="canvas" objectId={canvasId} />
      <CommentsSidebar environmentId={environmentId} objectType="canvas" objectId={canvasId} steps={c.steps} />
    </section>
  );
}
